/**
 * SupplyVue Planner — the Material UI 9 theme. Also the Modelling Portal's
 * theme: copied verbatim from the Planner Portal's `src/design/theme.ts`, so
 * keep the two copies identical.
 *
 * Verified against @mui/material 9.4.0 and MUI X 9.14.0 (npm, 2026-09-21).
 *
 * One source of truth: the values come from `tokens.ts`, and every
 * `styleOverrides` rule below refers to the CSS custom property rather than the
 * literal, so `tokens.css` and this file cannot drift apart. Import tokens.css
 * once at the app root, before this theme is applied.
 *
 * Mount it like this — the provider wrapper matters as much as the object:
 *
 *   import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
 *   import CssBaseline from '@mui/material/CssBaseline';
 *   import './tokens.css';
 *   import { theme } from './theme';
 *
 *   // NOTE: createTheme is called at module scope, NOT in a render body.
 *   // The Planner currently calls it inside AppThemeProvider on every render.
 *   <StyledEngineProvider enableCssLayer>
 *     <ThemeProvider theme={theme} disableTransitionOnChange>
 *       <CssBaseline />
 *       …
 *     </ThemeProvider>
 *   </StyledEngineProvider>
 */

import { createTheme, type Theme } from '@mui/material/styles'
// Side-effect import: adds MuiDataGrid to Components<Theme> so the override
// below type-checks. Types only — nothing is emitted.
import type {} from '@mui/x-data-grid-premium/themeAugmentation'
import {
  TOKENS,
  FONT_SANS,
  FONT_MONO,
  SIZES,
  CHART_CATEGORICAL,
} from './tokens'

const v = (name: string) => `var(--${name})`

export const theme: Theme = createTheme({
  /* ── v9 platform switches ───────────────────────────────────────────── */

  /** CSS theme variables. `theme.vars.palette.primary.main` resolves to a
   *  custom property, so a style override never bakes a literal in. */
  cssVariables: { cssVarPrefix: 'svp', colorSchemeSelector: 'data' },

  /** Cascade layers. MUI emits @layer mui.global / .components / .theme /
   *  .custom / .sx. This is the migration's safety net: while the old inline
   *  `sx` and the new theme coexist, the theme layer wins predictably and the
   *  36 `!important` declarations in the current code can come out one at a
   *  time rather than all at once. Pair it with <StyledEngineProvider enableCssLayer>. */
  modularCssLayers: true,

  /* focusVisible lives in tokens.css as a :focus-visible rule: the theme key is
     MUI 9 only and this Portal is on 7.3.2. Restore it here on a v9 upgrade. */

  /* ── foundations ────────────────────────────────────────────────────── */

  /** 4px base. WARNING: this halves every existing `sx` shorthand —
   *  `p: 3` was 24px and is now 12px. See Migration, phase 2. */
  spacing: 4,

  shape: { borderRadius: 3 },

  /** A desktop instrument. Phones are out of scope; 1280 is the design floor. */
  breakpoints: { values: { xs: 0, sm: 1024, md: 1280, lg: 1440, xl: 1920 } },

  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: {
          main: TOKENS.brandTeal,
          dark: TOKENS.teal700,
          light: TOKENS.teal300,
          contrastText: TOKENS.surface0,
        },
        secondary: {
          main: TOKENS.brandBlue,
          dark: TOKENS.blue600,
          light: TOKENS.blue50,
          contrastText: TOKENS.surface0,
        },
        // The Scheduler's state colours replace MUI's stock #4caf50 / #2196f3 /
        // #ff9800 / #f44336, which were copied in verbatim and never chosen.
        success: {
          main: TOKENS.stateComplete,
          light: TOKENS.stateCompleteTint,
          dark: TOKENS.stateCompleteFg,
          contrastText: TOKENS.surface0,
        },
        warning: {
          main: TOKENS.stateRunning,
          light: TOKENS.stateRunningTint,
          dark: TOKENS.amberFg,
          contrastText: TOKENS.surface0,
        },
        error: {
          main: TOKENS.stateFailed,
          light: TOKENS.stateFailedTint,
          dark: TOKENS.stateFailedFg,
          contrastText: TOKENS.surface0,
        },
        info: {
          main: TOKENS.brandBlue,
          light: TOKENS.blue50,
          dark: TOKENS.blue600,
          contrastText: TOKENS.surface0,
        },
        text: {
          primary: TOKENS.ink900,
          secondary: TOKENS.ink500, // was #434343; ink-400 is NOT used for text (4.07:1)
          disabled: TOKENS.ink300,
        },
        divider: TOKENS.line200,
        background: { default: TOKENS.surface50, paper: TOKENS.surface0 },
        // The old `borders` palette group is gone with its module augmentation
        // (Theme/theme.d.ts): line-200 and line-400 are the tokens now.
      },
    },
  },

  typography: {
    fontFamily: FONT_SANS,
    htmlFontSize: 16,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    // The three-step ladder. h1 25 / h2 22 / h3 18 / h4 16 / h5 14 collapses to
    // display 22 / title 16 / subtitle 14. h4 and h5 are mapped so existing
    // `variant="h4"` call sites keep working through the migration.
    h1: {
      fontSize: 22,
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontSize: 22,
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.005em',
    },
    h4: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.005em',
    },
    h5: {
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
    },
    // h6, body2, subtitle1 and caption were never declared and are used in the
    // code, so they currently fall back to Roboto metrics. Declared here.
    h6: {
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    subtitle1: { fontSize: 13, fontWeight: 600, lineHeight: 1.45 },
    subtitle2: {
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    body1: { fontSize: 13, fontWeight: 400, lineHeight: 1.45 },
    body2: { fontSize: 12, fontWeight: 400, lineHeight: 1.4 },
    caption: { fontSize: 11, fontWeight: 400, lineHeight: 1.4 },
    overline: {
      fontSize: 10.5,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    button: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
  },

  /** Three levels, not twenty-five. Index 1 = panel, 2 = sticky, 3+ = pop. */
  shadows: [
    'none',
    '0 1px 2px rgba(14,27,34,0.06)',
    '0 2px 4px rgba(14,27,34,0.08)',
    ...(Array(22).fill('0 6px 22px rgba(14,27,34,0.16)') as string[]),
  ] as Theme['shadows'],

  components: {
    /* ── global ───────────────────────────────────────────────────────── */
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFeatureSettings: '"tnum" 1',
          backgroundColor: v('surface-50'),
          color: v('ink-900'),
        },
        // Density is a data attribute, not a MUI API: there is no theme-level
        // density option in v9 (the docs describe a defaultProps pattern and a
        // real API is an open RFC). tokens.css re-points --row-h / --pad / --tap.
        ':root': { colorScheme: 'light' },
      },
    },

    /* ── actions ──────────────────────────────────────────────────────── */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        size: 'medium',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          height: SIZES.controlH,
          padding: '0 var(--space-12)',
          gap: 'var(--space-6)',
          borderRadius: v('radius-md'),
          transition:
            'background-color var(--motion-fast) var(--motion-easing), border-color var(--motion-fast) var(--motion-easing)',
          '&.Mui-disabled': { opacity: v('opacity-disabled') },
        },
        sizeSmall: {
          height: SIZES.controlHSm,
          padding: '0 var(--space-10)',
          fontSize: 11,
        },
        contained: {
          backgroundColor: v('brand-teal'),
          '&:hover': { backgroundColor: v('brand-teal-700') },
        },
        outlined: {
          borderColor: v('line-400'),
          color: v('ink-700'),
          '&:hover': {
            backgroundColor: v('surface-50'),
            borderColor: v('line-400'),
          },
        },
        text: {
          color: v('ink-600'),
          '&:hover': { backgroundColor: v('line-100') },
        },
        startIcon: { marginRight: 0, '& > *:first-of-type': { fontSize: 15 } },
        endIcon: { marginLeft: 0, '& > *:first-of-type': { fontSize: 15 } },
      },
    },
    MuiIconButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          width: SIZES.controlH,
          height: SIZES.controlH,
          borderRadius: v('radius-sm'),
          color: v('ink-500'),
          '&:hover': { backgroundColor: v('line-100'), color: v('ink-700') },
        },
        sizeSmall: { width: SIZES.controlHSm, height: SIZES.controlHSm },
      },
    },

    /* ── forms ────────────────────────────────────────────────────────── */
    MuiTextField: {
      // v9: InputProps / InputLabelProps are removed. Use slotProps.
      defaultProps: {
        variant: 'outlined',
        size: 'small',
        slotProps: { inputLabel: { shrink: true } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: SIZES.controlH,
          borderRadius: v('radius-sm'),
          backgroundColor: v('surface-0'),
          fontSize: 12,
          // line-400, not line-300: line-300 is 1.57:1 on white and misses the
          // 3:1 floor for the boundary of a control.
          '& .MuiOutlinedInput-notchedOutline': { borderColor: v('line-400') },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: v('ink-400'),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: v('brand-teal'),
            borderWidth: 1,
            boxShadow: `inset 0 0 0 1px ${v('brand-teal')}`,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: v('state-failed'),
          },
          '&.Mui-disabled': { backgroundColor: v('surface-50') },
        },
        input: {
          padding: '0 var(--space-8)',
          height: '100%',
          boxSizing: 'border-box',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          marginBottom: 'var(--space-4)',
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: v('ink-500'),
          '&.Mui-focused': { color: v('ink-500') },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginTop: 'var(--space-4)',
          fontSize: 12,
          color: v('ink-500'),
          '&.Mui-error': { color: v('state-failed-fg') },
        },
      },
    },
    MuiSelect: {
      // MUI 7 spelling. On v9 this becomes slotProps.menu.
      defaultProps: {
        MenuProps: {
          slotProps: {
            paper: {
              sx: {
                mt: '3px',
                borderRadius: v('radius-sm'),
                border: `1px solid ${v('line-200')}`,
                boxShadow: v('shadow-pop'),
                maxHeight: 260,
              },
            },
          },
        },
      },
      styleOverrides: {
        select: {
          paddingTop: 0,
          paddingBottom: 0,
          display: 'flex',
          alignItems: 'center',
        },
        icon: { color: v('ink-400') },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 12,
          minHeight: 32,
          padding: '7px var(--space-10)',
          color: v('ink-700'),
          '&:hover': { backgroundColor: v('line-100') },
          '&.Mui-selected': {
            backgroundColor: v('brand-blue-50'),
            color: v('brand-blue-600'),
            fontWeight: 600,
            '&:hover': { backgroundColor: v('brand-blue-50') },
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: { size: 'small', disableCloseOnSelect: true },
      styleOverrides: {
        paper: {
          borderRadius: v('radius-sm'),
          border: `1px solid ${v('line-200')}`,
          boxShadow: v('shadow-pop'),
        },
        listbox: { padding: 'var(--space-4) 0', maxHeight: 260 },
        option: {
          fontSize: 11.5,
          minHeight: 32,
          '&[aria-selected="true"]': { backgroundColor: v('brand-blue-50') },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: { disableRipple: true, size: 'small' },
      styleOverrides: {
        root: {
          color: v('line-400'),
          padding: 'var(--space-4)',
          '&.Mui-checked': { color: v('brand-teal') },
        },
      },
    },
    MuiRadio: {
      defaultProps: { disableRipple: true, size: 'small' },
      styleOverrides: {
        root: {
          color: v('line-400'),
          padding: 'var(--space-4)',
          '&.Mui-checked': { color: v('brand-teal') },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 34, height: 18, padding: 0 },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            '& + .MuiSwitch-track': {
              backgroundColor: v('brand-teal'),
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 14,
          height: 14,
          boxShadow: '0 1px 2px rgba(14,27,34,0.3)',
        },
        track: {
          borderRadius: v('radius-pill'),
          backgroundColor: v('line-300'),
          opacity: 1,
        },
      },
    },

    /* ── navigation ───────────────────────────────────────────────────── */
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 36, borderBottom: `1px solid ${v('line-200')}` },
        indicator: { height: 2, backgroundColor: v('brand-teal') },
      },
    },
    MuiTab: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          minHeight: 36,
          padding: '9px var(--space-12)',
          fontSize: 12,
          fontWeight: 600,
          color: v('ink-500'),
          textTransform: 'none',
          '&:hover': { color: v('ink-700') },
          '&.Mui-selected': { color: v('brand-teal') },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: 12.5 },
        separator: { color: v('ink-300'), marginLeft: 6, marginRight: 6 },
        li: {
          '& a': {
            color: v('brand-blue-600'),
            fontWeight: 600,
            textDecoration: 'none',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'primary' },
      styleOverrides: {
        root: { height: SIZES.brandBarH, justifyContent: 'center' },
      },
    },
    MuiStepper: { styleOverrides: { root: { padding: 0 } } },

    /* ── surfaces and overlays ────────────────────────────────────────── */
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', borderRadius: v('radius-md') },
        outlined: { borderColor: v('line-200') },
      },
    },
    MuiDialog: {
      // v9: disableEscapeKeyDown is removed — inspect `reason` in onClose instead.
      defaultProps: {
        slotProps: {
          paper: { elevation: 0 },
          backdrop: { sx: { backgroundColor: `rgba(14,27,34,${0.44})` } },
        },
      },
      styleOverrides: {
        paper: {
          border: `1px solid ${v('line-300')}`,
          borderRadius: v('radius-md'),
          boxShadow: v('shadow-pop'),
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '15px 18px',
          fontSize: 15,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          borderBottom: `1px solid ${v('line-200')}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '14px 18px', backgroundColor: v('surface-50') },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 18px',
          borderTop: `1px solid ${v('line-200')}`,
          gap: 'var(--space-8)',
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: false, enterDelay: 300 },
      styleOverrides: {
        tooltip: {
          backgroundColor: v('ink-900'),
          color: v('surface-0'),
          borderRadius: v('radius-sm'),
          padding: '7px 9px',
          fontSize: 11.5,
          lineHeight: 1.4,
          maxWidth: 260,
          boxShadow: v('shadow-pop'),
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        autoHideDuration: 5000,
      },
    },
    MuiAlert: {
      defaultProps: { variant: 'standard', iconMapping: {} },
      styleOverrides: {
        root: {
          borderRadius: v('radius-md'),
          border: `1px solid ${v('line-200')}`,
          borderLeftWidth: 3,
          fontSize: 12.5,
          lineHeight: 1.45,
          padding: 'var(--space-10) var(--space-12)',
          alignItems: 'flex-start',
        },
        standardInfo: {
          backgroundColor: v('brand-blue-50'),
          borderLeftColor: v('brand-blue'),
          color: v('ink-700'),
        },
        standardSuccess: {
          backgroundColor: v('state-complete-tint'),
          borderLeftColor: v('state-complete'),
          color: v('state-complete-fg'),
        },
        standardWarning: {
          backgroundColor: v('state-running-tint'),
          borderLeftColor: v('state-running'),
          color: v('amber-fg'),
        },
        standardError: {
          backgroundColor: v('state-failed-tint'),
          borderLeftColor: v('state-failed'),
          color: v('state-failed-fg'),
        },
        icon: { padding: 0, marginRight: 'var(--space-10)', fontSize: 16 },
      },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          height: 'auto',
          borderRadius: v('radius-md'),
          border: '1px solid',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          lineHeight: 1.6,
        },
        label: { padding: '1px 7px' },
        // Tones are applied with a `color` prop; the -fg tokens carry the text
        // because the solid state colours are 2.86–4.33:1 on their own tints.
        colorDefault: {
          backgroundColor: v('surface-50'),
          color: v('ink-700'),
          borderColor: v('line-300'),
        },
        colorSuccess: {
          backgroundColor: v('state-complete-tint'),
          color: v('state-complete-fg'),
          borderColor: v('state-complete'),
        },
        colorWarning: {
          backgroundColor: v('state-running-tint'),
          color: v('amber-fg'),
          borderColor: v('state-running'),
        },
        colorError: {
          backgroundColor: v('state-failed-tint'),
          color: v('state-failed-fg'),
          borderColor: v('state-failed'),
        },
        colorPrimary: {
          backgroundColor: v('teal-tint'),
          color: v('brand-teal'),
          borderColor: v('brand-teal-300'),
        },
        colorInfo: {
          backgroundColor: v('brand-blue-50'),
          color: v('brand-blue-600'),
          borderColor: v('brand-blue'),
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: v('line-200') } } },
    MuiSkeleton: {
      defaultProps: { animation: 'pulse' },
      styleOverrides: {
        root: { backgroundColor: v('line-100'), borderRadius: v('radius-sm') },
      },
    },

    /* ── MUI X Data Grid ──────────────────────────────────────────────── */
    MuiDataGrid: {
      defaultProps: {
        rowHeight: SIZES.rowH, // 28. The Scheduler declares this and never applies it.
        columnHeaderHeight: SIZES.gridHeaderH,
        disableRowSelectionOnClick: true,
        pageSizeOptions: [25, 50, 100],
      },
      styleOverrides: {
        root: {
          border: `1px solid ${v('line-200')}`,
          borderRadius: v('radius-md'),
          fontFamily: FONT_SANS,
          color: v('ink-900'),
          '--DataGrid-rowBorderColor': v('line-100'),
          // Opt-in column accents, carried over from SCHEDULER_GRID_SX. These
          // are nested under `root`: styleOverrides' own keys must be slot
          // names, so a bare selector at the top level does not type-check.
          '& .svp-mono': {
            fontFamily: FONT_MONO,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
          },
          '& .svp-num': {
            fontFamily: FONT_MONO,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
            justifyContent: 'flex-end',
          },
          '& .svp-link': {
            fontFamily: FONT_MONO,
            color: v('brand-blue-600'),
            fontWeight: 600,
            letterSpacing: '-0.02em',
          },
          '& .svp-needs-input': {
            backgroundColor: v('surface-warn'),
            boxShadow: `inset 2px 0 0 ${v('state-running')}`,
          },
          // Promote the grid to its own layer. Without this a long grid
          // repaints its rows on scroll and flickers.
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          // The scroller and main panel carry the row ground so a fast scroll
          // does not flash the page behind the rows it has not drawn yet.
          '& .MuiDataGrid-virtualScroller': { backgroundColor: v('surface-0') },
          '& .MuiDataGrid-main': { backgroundColor: v('surface-0') },
          '& .MuiDataGrid-columnHeader--pinnedLeft': {
            backgroundColor: v('panel-head'),
          },
          // A singleSelect editor sizes to its cell rather than its content.
          '& .MuiDataGrid-cell--editing .MuiSelect-root': {
            width: '100%',
            minWidth: '100%',
          },
          '& .MuiDataGrid-cell--editing .MuiSelect-select': {
            padding: '8px 12px',
            minHeight: 'auto',
          },
          '& .MuiSelect-paper': { maxWidth: 200, minWidth: 150 },
        },
        columnHeaders: { borderBottom: `1px solid ${v('line-300')}` },
        columnHeader: {
          backgroundColor: v('panel-head'),
          borderRadius: 0,
          '&:focus, &:focus-within': { outline: 'none' },
        },
        // ink-500, not ink-400: ink-400 on panel-head is 3.68:1 and these
        // labels are 10.5px.
        columnHeaderTitle: {
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: v('ink-500'),
        },
        columnSeparator: { display: 'none' },
        cell: {
          fontSize: 12,
          borderRight: 'none',
          '&:focus, &:focus-within': { outline: 'none' },
        },
        row: {
          backgroundColor: v('surface-0'),
          // Virtualisation recycles row elements, so :nth-of-type stops being
          // reliable once a long grid scrolls. Where a grid stamps row--odd /
          // row--even onto its rows those win; nth-of-type is the fallback for
          // grids that do not.
          '&:nth-of-type(odd)': { backgroundColor: v('surface-50') },
          '&.row--even': { backgroundColor: v('surface-0') },
          '&.row--odd': { backgroundColor: v('surface-50') },
          // A parent row in a nested grid (Total production's batches): a
          // heading for the rows under it, so it doesn't stripe.
          '&.row--parent': {
            backgroundColor: v('panel-head'),
            fontWeight: 600,
          },
          '&.row--edited': { backgroundColor: v('surface-warn') },
          '&:hover': { backgroundColor: v('brand-blue-50') },
          // Cells take the row's colour so a zebra stripe reads across the row
          // rather than stopping at each cell boundary.
          '& .MuiDataGrid-cell': { backgroundColor: 'inherit' },
          // A derived value the line-settings preview changed: not saved yet,
          // and not typed by the planner (that row is row--edited).
          '& .MuiDataGrid-cell.cell--recalculated': {
            backgroundColor: v('brand-blue-50'),
            color: v('brand-blue-600'),
            fontWeight: 600,
          },
          // An edit the rules refuse (line-settings preview): Save is blocked
          // until it changes. Outlined too, so it reads without colour.
          '& .MuiDataGrid-cell.cell--problem': {
            backgroundColor: v('state-failed-tint'),
            color: v('state-failed-fg'),
            fontWeight: 600,
            boxShadow: `inset 0 0 0 1px ${v('state-failed')}`,
          },
          // The same problem, there before these edits: shown, not blocking.
          '& .MuiDataGrid-cell.cell--notice': {
            backgroundColor: v('amber-tint'),
            color: v('amber-fg'),
          },
          // The #CBD5E1 editable-cell fill is gone: it made *editable* louder
          // than *edited* and turned a dense grid into a checkerboard.
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: 'transparent',
            cursor: 'pointer',
          },
          // Pinned cells keep their own ground so the frozen column stays
          // legible over a stripe. Declared last and one step more specific
          // than the zebra rules above, which is what makes them win.
          '& .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: v('surface-0'),
          },
          '&.row--odd .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: v('surface-0'),
          },
          '&.row--even .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: v('surface-0'),
          },
          '&.row--edited .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: v('surface-0'),
          },
        },
        footerContainer: {
          minHeight: 40,
          borderTop: `1px solid ${v('line-200')}`,
          fontSize: 12,
        },
      },
    },

    /* MUI X Charts theme keys (MuiChartsAxis, MuiChartsGrid, MuiChartsLegend,
       MuiChartsTooltip) are held in the design system's handoff/theme.ts and
       land with @mui/x-charts. Without the package their keys are not in
       Components<Theme> and will not type-check. */
  },
})

/** The chart palette, passed to every chart's `colors` prop. Slots are assigned
 *  in order and never cycled: a ninth series folds into "Other" or the chart
 *  becomes small multiples. Validated (lightness band, chroma floor, CVD ΔE 13.0
 *  under protanopia and deuteranopia, normal-vision ΔE 18.7, 3:1 contrast) on
 *  both surface-0 and surface-50. */
export const chartColors = [...CHART_CATEGORICAL]

export default theme
